import type { Supplier } from '../../types'
import { riskBand, supplierDisplayName } from './supplierRollup'

/** Same 14 parameters as Intelligence view risk heatmap. */
export const DIMENSION_KEYS = [
  { key: 'strategic', label: 'Strategic' },
  { key: 'financial', label: 'Financial' },
  { key: 'compliance', label: 'Compliance & Legal' },
  { key: 'operational', label: 'Operational' },
  { key: 'geopolitical', label: 'Geopolitical' },
  { key: 'infra', label: 'Infrastructure' },
  { key: 'supply_chain', label: 'Supply chain' },
  { key: 'esg', label: 'ESG' },
  { key: 'labor', label: 'Labor' },
  { key: 'health_biological', label: 'Health & Bio' },
  { key: 'natural_hazard', label: 'Natural Hazard' },
  { key: 'market', label: 'Market' },
  { key: 'tech_innovation', label: 'Technology' },
  { key: 'cyber', label: 'Cybersecurity' },
] as const

export type RiskRatingSupplier = Pick<
  Supplier,
  | 'name'
  | 'display_name'
  | 'product_line_names'
  | 'location'
  | 'kenvue_region'
  | 'category'
  | 'brand'
  | 'overall_score'
  | 'risk_gauge'
  | 'risk_scores'
  | 'risk_dimension_narratives'
  | 'highest_risk_areas'
  | 'supplier_unique_id'
  | 'single_source'
  | 'updated_at'
  | 'procurement_type'
  | 'parent_name'
  | 'spend_k'
  | 'dm_em'
>

export function scoreBandWord(value: number): 'High' | 'Medium' | 'Low' {
  const b = riskBand(value)
  if (b === 'high') return 'High'
  if (b === 'medium') return 'Medium'
  return 'Low'
}

/** Highest score in a category, with the band taken from the number. */
export function formatHighestScoreSentence(name: string, scope: string, score: number): string {
  const word = scoreBandWord(score)
  const caveat = word === 'High' ? '' : ` That score is ${word}, not high risk.`
  return `${name} has the highest score in ${scope}: ${Number(score).toFixed(2)} / 10 (${word}).${caveat} Next question: “Tell me about ${name}”.`
}

/** Highest-risk areas for one supplier: stored text, else the top dimension scores. */
export function formatHighestRiskAreasSummary(s: RiskRatingSupplier): string {
  const name = supplierDisplayName(s) || 'This supplier'
  const stored = (s.highest_risk_areas || '').trim()
  let body: string
  if (stored) {
    body = `${name}: the highest-risk areas are ${stored}.`
  } else {
    const ranked: { label: string; value: number }[] = []
    for (const { key, label } of DIMENSION_KEYS) {
      const value = scoreValue(s, key)
      if (value != null) ranked.push({ label, value })
    }
    ranked.sort((a, b) => b.value - a.value)
    const top = ranked.slice(0, 3)
    if (top.length === 0) {
      body = `${name}: no highest-risk areas are stored in platform data.`
    } else {
      const bits = top.map((d) => `${d.label} ${d.value} (${scoreBandWord(d.value)})`)
      body = `${name}: the highest scores are ${bits.join(', ')}.`
    }
  }
  return `${body} Next question: “What questions should I ask ${name}?”`
}

export function scoreValue(s: Pick<Supplier, 'risk_scores'>, dim: string): number | null {
  const v = s.risk_scores?.[dim]
  if (v === undefined || v === null) return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

export function hasAnyRiskScores(s: Pick<Supplier, 'risk_scores' | 'overall_score' | 'risk_gauge'>): boolean {
  if (s.overall_score != null && typeof s.overall_score === 'number' && Number.isFinite(s.overall_score)) return true
  if (s.risk_gauge) return true
  const rs = s.risk_scores
  if (rs && typeof rs === 'object') {
    for (const v of Object.values(rs)) {
      if (v != null && typeof v === 'number' && Number.isFinite(v)) return true
      if (v != null && typeof v !== 'number' && Number.isFinite(Number(v))) return true
    }
  }
  return false
}

function bandDisplay(value: number | null): string {
  if (value == null) return '—'
  return scoreBandWord(value)
}

function overallBandLabel(s: RiskRatingSupplier): string {
  if (s.overall_score != null && Number.isFinite(Number(s.overall_score))) {
    const b = riskBand(Number(s.overall_score))
    if (b === 'high') return 'HIGH RISK'
    if (b === 'medium') return 'MEDIUM RISK'
    return 'LOW RISK'
  }
  const g = (s.risk_gauge ?? '').toUpperCase()
  if (g.includes('HIGH')) return 'HIGH RISK'
  if (g.includes('MODERATE') || g.includes('MEDIUM')) return 'MEDIUM RISK'
  if (g.includes('LOW')) return 'LOW RISK'
  if (s.risk_gauge?.trim()) return s.risk_gauge.trim().toUpperCase()
  return ''
}

function formatUpdated(iso: string | undefined | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * Build GFM markdown matching Intelligence view overview:
 * header meta + overall score + all 14 heatmap parameters with band.
 */
export function formatSupplierRiskRatingsMarkdown(s: RiskRatingSupplier): string {
  const overallBand = overallBandLabel(s)
  const meta = [s.location, s.kenvue_region, s.category].filter(Boolean).join(' · ')
  const overall =
    s.overall_score != null && Number.isFinite(Number(s.overall_score))
      ? String(Number(s.overall_score))
      : null
  const gauge = s.risk_gauge?.trim() || null

  const title = supplierDisplayName(s) || 'Supplier'
  const parentLabel = (s.parent_name || '').trim()
  const showParent = !!parentLabel && parentLabel.toLowerCase() !== title.toLowerCase()
  const entityNames = (s.product_line_names || [])
    .map((n) => n.trim())
    .filter((n) => n && n.toLowerCase() !== title.toLowerCase())
  const detailBits: string[] = []
  if (s.procurement_type === 'indirect') detailBits.push('Indirect')
  else if (s.procurement_type === 'direct' || s.procurement_type == null) detailBits.push('Direct')
  if (showParent) detailBits.push(`Parent: ${parentLabel}`)
  if (entityNames.length) detailBits.push(`Entities: ${entityNames.join(', ')}`)
  if (s.spend_k != null && Number.isFinite(Number(s.spend_k))) {
    detailBits.push(`Spend: $${Number(s.spend_k).toLocaleString(undefined, { maximumFractionDigits: 1 })}K`)
  }
  if (s.dm_em) detailBits.push(`DM/EM: ${s.dm_em}`)
  if (s.brand) detailBits.push(`Brand: ${s.brand}`)
  if (s.single_source === true) detailBits.push('Single-source')
  else if (s.single_source === false) detailBits.push('Multi-source')
  if (s.supplier_unique_id) detailBits.push(`ID: ${s.supplier_unique_id}`)
  const updated = formatUpdated(s.updated_at)
  if (updated) detailBits.push(`Updated ${updated}`)

  const lines: string[] = []
  lines.push(overallBand ? `${title} · ${overallBand}` : title)
  if (meta) lines.push(meta)
  if (detailBits.length) lines.push(detailBits.join(' · '))
  lines.push('')

  const overallScoreLine = [overall != null ? `${overall} / 10` : null, gauge].filter(Boolean).join(' · ')
  if (overallScoreLine) {
    lines.push(`Overall: ${overallScoreLine}`)
    lines.push('')
  }

  lines.push('Risk heatmap (14 categories)')
  lines.push('')
  lines.push('| Parameter | Rating (/10) | Band | Commentary |')
  lines.push('| --- | --- | --- | --- |')

  let anyScore = false
  let anyNarr = false
  const narr = s.risk_dimension_narratives ?? {}
  for (const { key, label } of DIMENSION_KEYS) {
    const v = scoreValue(s, key)
    if (v != null) anyScore = true
    const text = (narr[key] ?? '').trim()
    if (text) anyNarr = true
    const cell = text ? text.replace(/\|/g, '\\|').replace(/\n+/g, ' ') : '—'
    lines.push(`| ${label} | ${v != null ? String(v) : '—'} | ${bandDisplay(v)} | ${cell} |`)
  }

  if (!anyScore && overall == null) {
    return [title, meta, '', 'No parameter ratings were found for this supplier in platform data.']
      .filter(Boolean)
      .join('\n')
  }

  lines.push('')
  lines.push(
    anyNarr
      ? 'Source: Intelligence view (scores + risk commentaries).'
      : 'Source: Intelligence view (same scores as the risk heatmap). No commentaries in platform data for these parameters.'
  )
  return lines.join('\n')
}

/** Named-supplier scorecard should render as DbTableChatCard, not the heatmap profile. */
export function wantsSupplierRatingsTable(text: string): boolean {
  const t = text.toLowerCase()
  if (/\bin\s+a\s+table\b/.test(t) || /\bas\s+a\s+table\b/.test(t)) return true
  return t.includes('ratings table') || t.includes('rating table')
}

/** Explicit scorecard ask even when supplier name is missing. */
export function isExplicitScorecardAsk(text: string): boolean {
  const t = text.toLowerCase()
  return (
    t.includes('risk assessment') ||
    t.includes('by rating') ||
    t.includes('ratings table') ||
    t.includes('rating table') ||
    t.includes('heatmap') ||
    t.includes('scorecard') ||
    (wantsSupplierRatingsTable(text) && /\brisk\b/.test(t)) ||
    (t.includes('table') && (t.includes('rating') || t.includes('assessment') || t.includes('score')))
  )
}

/**
 * Named-supplier asks that should use Intelligence view DB scores.
 * Broader than explicit scorecard (includes “information for…”).
 */
export function isSupplierScorecardAsk(text: string): boolean {
  if (isDimensionCommentaryAsk(text)) return false
  if (isPortfolioSupplierListAsk(text)) return false
  if (isExplicitScorecardAsk(text)) return true
  const t = text.toLowerCase()
  return (
    t.includes('rating') ||
    t.includes('ratings') ||
    t.includes('assessment') ||
    t.includes('parameter') ||
    t.includes('score') ||
    t.includes('scores') ||
    t.includes('know about') ||
    t.includes('tell me about') ||
    t.includes('information for') ||
    t.includes('information about') ||
    t.includes('details for') ||
    t.includes('details about') ||
    t.includes('profile for') ||
    t.includes('profile of') ||
    t.includes('risk profile') ||
    /\brisk\b/.test(t)
  )
}

/** Portfolio list / filter asks — not a single-supplier scorecard. */
export function isPortfolioSupplierListAsk(text: string): boolean {
  const t = text.toLowerCase()
  if (!/\bsuppliers?\b/.test(t) && !/\btable of\b/.test(t)) return false
  return (
    /\b(high|medium|low)[- ]?risk\b/.test(t) ||
    /\bsingle[- ]source\b/.test(t) ||
    /\bsole[- ]source\b/.test(t) ||
    /\b(direct|indirect)(\s+procurement)?\b/.test(t) ||
    /\b(emea|apac|latam|\bna\b|usa|uk|germany|france|india|packaging|chemicals)\b/.test(t) ||
    /\b(give|show|list|find|name)\b[\w\s]*\btable\b/.test(t)
  )
}

/** @deprecated use isExplicitScorecardAsk / isSupplierScorecardAsk */
export function isRiskRatingsTableRequest(text: string): boolean {
  return isExplicitScorecardAsk(text) || isSupplierScorecardAsk(text)
}

/**
 * Extract a supplier name guess from natural language.
 * Examples: "risk assessment of Gissler & Pass GmbH in a table"
 */
export function extractSupplierNameCandidate(text: string): string | null {
  const cleaned = text.replace(/\s+/g, ' ').trim()
  if (!cleaned) return null

  const patterns: RegExp[] = [
    /\b(?:what\s+(?:are\s+)?(?:the\s+)?)?brands?\s+(?:associated\s+with|for|of|with)\s+(.+)$/i,
    /\bwhich\s+brands?\s+(?:are\s+)?(?:associated\s+with|for|of|with)\s+(.+)$/i,
    /\bassociated\s+with\s+(.+)$/i,
    /\b(?:commentary|commentaries|narrative|narratives)\s+(?:of|for|on)\s+(?:the\s+)?(?:parameter\s+)?(?:.+?\s+)?(?:of|for)\s+(.+)$/i,
    /\b(?:commentary|commentaries)\s+(?:of|for|on)\s+(.+)$/i,
    /\b(?:explain|why)\s+(?:.+?\s+)?(?:for|of)\s+(.+)$/i,
    /\b(?:risk\s+assessment|assessment|ratings?|information|details|profile)\s+(?:of|for)\s+(.+)$/i,
    /\b(?:show|give|get)\s+(?:me\s+)?(.+?)\s+risk\s+profile\b/i,
    /^(.+?)\s+risk\s+profile\b/i,
    /\brisk\s+profile\s+(?:of|for)\s+(.+)$/i,
    /\b(?:know about|tell me about|information about|details about)\s+(.+)$/i,
    // "Questions to ask Gissler & Pass" / "What questions should I ask CHEP?"
    /\bquestions?\s+to\s+ask\s+(.+)$/i,
    /\bask\s+my\s+supplier\s+(.+)$/i,
    /\b(?:should\s+i\s+)?ask\s+(?!my\s+supplier\b)(.+)$/i,
    /\b(?:of|for)\s+(.+?)(?:\s+(?:in\s+a\s+table|as\s+a\s+table|by\s+rating|in\s+table|please|thanks)|\s*[.?!]?\s*$)/i,
  ]

  for (const re of patterns) {
    const m = cleaned.match(re)
    if (m?.[1]) {
      if (/\[supplier\]/i.test(m[1])) return null
      const name = sanitizeNameCandidate(m[1])
      if (name) return name
    }
  }

  return null
}

/** Collapse CRLF / whitespace noise before tokenizing or ranking legal names. */
export function normalizeSupplierName(value: string): string {
  return value.replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim()
}

/** Significant name tokens for ilike search (handles "&" in names). Keep length ≥ 2 so "MM" is kept. */
export function supplierSearchTokens(nameQuery: string): string[] {
  const stop = new Set([
    'gmbh',
    'ltd',
    'llc',
    'inc',
    'sas',
    'sa',
    'co',
    'the',
    'and',
    'of',
    'for',
  ])
  return normalizeSupplierName(nameQuery)
    .split(/[\s&,./]+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 2 && !stop.has(t.toLowerCase()))
}

/** Prefer longest token for broad ilike primary (e.g. Packaging over MM). */
export function longestSupplierSearchToken(nameQuery: string): string | null {
  const tokens = supplierSearchTokens(nameQuery)
  if (tokens.length === 0) {
    const fallback = nameQuery.replace(/&/g, ' ').trim()
    return fallback || null
  }
  return tokens.reduce((a, b) => (b.length > a.length ? b : a), tokens[0])
}

export type RankedNameMatch<T extends { name?: string | null }> =
  | { kind: 'unique'; row: T }
  | { kind: 'ambiguous'; rows: T[] }
  | { kind: 'none' }

/**
 * Rank name matches: exact → starts-with query → all tokens → unique / ambiguous.
 * Dedupes by lowercased legal name, keeping the first occurrence in each group.
 */
export function rankSupplierNameMatches<T extends { name?: string | null }>(
  nameQuery: string,
  rows: T[]
): RankedNameMatch<T> {
  if (rows.length === 0) return { kind: 'none' }

  const byName = new Map<string, T>()
  for (const row of rows) {
    const key = normalizeSupplierName(row.name || '').toLowerCase()
    if (!key || byName.has(key)) continue
    byName.set(key, row)
  }
  const unique = [...byName.values()]
  if (unique.length === 0) return { kind: 'none' }

  const q = normalizeSupplierName(nameQuery).toLowerCase()
  const exact = unique.filter((r) => normalizeSupplierName(r.name || '').toLowerCase() === q)
  if (exact.length === 1) return { kind: 'unique', row: exact[0] }

  const starts = unique.filter((r) =>
    normalizeSupplierName(r.name || '').toLowerCase().startsWith(q)
  )
  if (starts.length === 1) return { kind: 'unique', row: starts[0] }

  const tokens = supplierSearchTokens(nameQuery)
  const tokenExact =
    tokens.length > 0
      ? unique.filter((r) => {
          const n = normalizeSupplierName(r.name || '').toLowerCase()
          return tokens.every((t) => n.includes(t.toLowerCase()))
        })
      : []
  if (tokenExact.length === 1) return { kind: 'unique', row: tokenExact[0] }

  // Prefer narrower shortlist for the disambiguation UI
  const shortlist =
    starts.length > 1 ? starts : tokenExact.length > 1 ? tokenExact : unique
  if (shortlist.length === 1) return { kind: 'unique', row: shortlist[0] }
  if (shortlist.length > 1) return { kind: 'ambiguous', rows: shortlist }
  return { kind: 'none' }
}

/** Map utterance dimension phrasing → DIMENSION_KEYS.key */
export function extractRiskDimensionKey(text: string): string | null {
  const t = text.toLowerCase().replace(/\s+/g, ' ')
  const aliases: Array<{ key: string; patterns: RegExp[] }> = [
    { key: 'strategic', patterns: [/\bstrategic\b/] },
    { key: 'financial', patterns: [/\bfinancial\b/, /\bfin\b/] },
    {
      key: 'compliance',
      patterns: [/\bcompliance\b/, /\blegal\b/, /\bcompliance\s*&\s*legal\b/],
    },
    { key: 'operational', patterns: [/\boperational\b/, /\bops\b/] },
    { key: 'geopolitical', patterns: [/\bgeopolitical\b/, /\bgeo\b/] },
    {
      key: 'infra',
      patterns: [/\binfrastructure\b/, /\binfra\b/, /\butilities\b/],
    },
    { key: 'supply_chain', patterns: [/\bsupply\s*chain\b/] },
    { key: 'esg', patterns: [/\besg\b/] },
    { key: 'labor', patterns: [/\blabor\b/, /\blabour\b/, /\bworkforce\b/] },
    {
      key: 'health_biological',
      patterns: [/\bhealth\b/, /\bbiological\b/, /\bbio\b/],
    },
    { key: 'natural_hazard', patterns: [/\bnatural\s*hazard\b/, /\bhazard\b/] },
    { key: 'market', patterns: [/\bmarket\b/] },
    {
      key: 'tech_innovation',
      patterns: [/\btechnology\b/, /\binnovation\b/, /\btech\b/],
    },
    { key: 'cyber', patterns: [/\bcyber\b/, /\bcybersecurity\b/] },
  ]
  for (const { key, patterns } of aliases) {
    if (patterns.some((re) => re.test(t))) return key
  }
  return null
}

/**
 * Supplier named next to one risk dimension, as in “CHEP strategic risk”.
 * Portfolio lists (“suppliers”) stay out.
 */
export function supplierBesideDimension(text: string): string | null {
  const key = extractRiskDimensionKey(text)
  if (!key) return null
  if (/\bsuppliers\b/i.test(text)) return null
  if (!/\brisk\b/i.test(text) && !/\bscore\b/i.test(text) && !/\bcommentary\b/i.test(text)) {
    return null
  }

  const label = DIMENSION_KEYS.find((d) => d.key === key)?.label ?? key
  const dimPhrase = [label, key.replace(/_/g, ' ')].filter((w) => w.length >= 3)
  const dimAlt = dimPhrase.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')

  // “tell me about the financial risk of Contract Pharmaceuticals”
  const structured = text.match(
    new RegExp(
      `\\b(?:tell\\s+me\\s+about|tell\\s+about|explain|describe)\\s+(?:the\\s+)?(?:${dimAlt})\\s+risk\\s+(?:of|for)\\s+(.+)$`,
      'i'
    )
  )
  if (structured?.[1]) {
    const fromStructured = sanitizeNameCandidate(structured[1])
    if (fromStructured) return fromStructured
  }

  let rest = text.replace(/\s+/g, ' ').trim()
  for (const w of dimPhrase) {
    rest = rest.replace(new RegExp(`\\b${w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'ig'), ' ')
  }
  rest = rest
    .replace(
      /\b(risk|score|scores|commentary|commentaries|narrative|narratives|for|of|on|the|only|just|show|give|me|tell|about|know|us|please|can|you|provide)\b/gi,
      ' '
    )
    .replace(/["'`]/g, ' ')
    .replace(/[?.!]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!rest) return null
  return sanitizeNameCandidate(rest)
}

/** Strip chat lead-ins left in a bad name slot before “no supplier” / re-resolve. */
export function cleanSupplierNameForLookup(query: string): string {
  let name = query.replace(/\s+/g, ' ').trim()
  for (let i = 0; i < 4; i++) {
    const next = name
      .replace(/^(tell|about|show|give|know|explain|describe|provide|can|you)(\s+(me|us|about|for))*\s+/i, '')
      .replace(/^["'`]+|["'`]+$/g, '')
      .trim()
    if (next === name) break
    name = next
  }
  return name
}

/** Ratings table for a named supplier (Parameter | Rating | Band | Commentary). */
export function buildSupplierScorecardTable(s: RiskRatingSupplier): {
  title: string
  headers: string[]
  rows: string[][]
  sourceLabel: string
} {
  const narr = s.risk_dimension_narratives ?? {}
  const rows = DIMENSION_KEYS.map(({ key, label }) => {
    const v = scoreValue(s, key)
    const text = (narr[key] ?? '').trim()
    return [label, v != null ? String(v) : '—', bandDisplay(v), text || '—']
  })
  const title = supplierDisplayName(s) || 'Supplier'
  return {
    title: `${title} — risk ratings`,
    headers: ['Parameter', 'Rating (/10)', 'Band', 'Commentary'],
    rows,
    sourceLabel: 'Intelligence view',
  }
}

/** Asks for dimension commentary / narratives (not the numeric scorecard alone). */
export function isDimensionCommentaryAsk(text: string): boolean {
  const t = text.toLowerCase()
  if (
    t.includes('commentary') ||
    t.includes('commentaries') ||
    t.includes('narrative') ||
    t.includes('narratives')
  ) {
    return true
  }
  if (/\bexplain\b/.test(t) && /\b(risk|rating|score|parameter|dimension)\b/.test(t)) {
    return true
  }
  if (/\bwhy\b/.test(t) && /\b(scored|score|rating|risk)\b/.test(t)) {
    return true
  }
  if (t.includes('parameter commentary') || t.includes('risk commentary')) return true
  return false
}

export function formatDimensionCommentaryMarkdown(
  s: RiskRatingSupplier,
  dimensionKey: string | null
): string {
  const title = supplierDisplayName(s) || 'Supplier'
  const narr = s.risk_dimension_narratives ?? {}
  const lines: string[] = []
  lines.push(`${title} — risk dimension commentary`)
  lines.push('')

  const dims = dimensionKey
    ? DIMENSION_KEYS.filter((d) => d.key === dimensionKey)
    : [...DIMENSION_KEYS]

  if (dimensionKey && dims.length === 0) {
    return `${title}\n\nNo matching risk parameter was recognized in that question.`
  }

  if (dimensionKey && dims.length === 1) {
    const { key, label } = dims[0]
    const v = scoreValue(s, key)
    const text = (narr[key] ?? '').trim()
    lines.push(`**${label}** — ${v != null ? `${v} / 10` : '—'} (${bandDisplay(v)})`)
    lines.push('')
    lines.push(text || 'No commentary in platform data for this parameter.')
    lines.push('')
    lines.push('Source: Intelligence view dimension commentaries.')
    lines.push('')
    lines.push(`Next question: “What is the highest risk in ${title}”`)
    return lines.join('\n')
  }

  lines.push('| Parameter | Rating (/10) | Commentary |')
  lines.push('| --- | --- | --- |')
  let anyNarr = false
  for (const { key, label } of dims) {
    const v = scoreValue(s, key)
    const text = (narr[key] ?? '').trim()
    if (text) anyNarr = true
    const cell = text
      ? text.replace(/\|/g, '\\|').replace(/\n+/g, ' ')
      : '—'
    lines.push(`| ${label} | ${v != null ? String(v) : '—'} | ${cell} |`)
  }
  lines.push('')
  if (!anyNarr) {
    lines.push('No commentary in platform data for these parameters.')
  } else {
    lines.push('Source: Intelligence view dimension commentaries.')
  }
  lines.push('')
  lines.push(`Next question: “What is the highest risk in ${title}”`)
  return lines.join('\n')
}

function sanitizeNameCandidate(raw: string): string | null {
  let name = cleanSupplierNameForLookup(raw)
  name = name.replace(/^(the|a|an)\s+/i, '')
  name = name.replace(/^(my\s+supplier|a\s+supplier|the\s+supplier)\s+/i, '')
  name = name.replace(/\s*[—–-]\s*full\s+supplier\s+profile\b.*$/i, '')
  name = name.replace(
    /\s+(in\s+a\s+table(?:\s+by\s+rating)?|as\s+a\s+table|by\s+rating|in\s+table|please|thanks|based\s+on\s+platform\b.*)$/i,
    ''
  )
  // Quick-action NBA suffix: "For CHEP, what are the next best actions…"
  name = name.replace(
    /\s*[,:]?\s*(what\s+are\s+the\s+next\s+best\b.*|next\s+best\s+actions?\b.*)$/i,
    ''
  )
  name = name.replace(/[.?!]+$/, '').trim()
  name = name
    .replace(/\s+(risk\s+assessment|ratings?|heatmap|scorecard|commentary|commentaries|narrative|narratives)\b.*$/i, '')
    .trim()
  // Strip leading dimension fragments: "Strategic Risk for MM Packaging" leftovers already handled by patterns
  name = name.replace(/^(?:the\s+)?(?:parameter\s+)?/i, '').trim()
  name = name.replace(/\s+table$/i, '').trim()
  if (name.length < 2) return null
  if (/\[supplier\]/i.test(name)) return null
  if (/^(my\s+supplier|a\s+supplier|the\s+supplier)$/i.test(name)) return null
  // Prefer markdown table instruction leftovers (quick-action suffix)
  if (/^based\s+on\s+platform\b/i.test(name)) return null
  // Chat verbs left in the slot are never a legal name
  if (/^(tell|about|show|give|know|explain|describe|provide)\b/i.test(name)) return null
  // Portfolio filter phrases mistaken for supplier names (e.g. "high risk EMEA packaging suppliers")
  if (
    /\b(high|medium|low)[- ]?risk\b/i.test(name) ||
    /\bsingle[- ]source\b/i.test(name) ||
    /\bsuppliers?\b/i.test(name) ||
    /\b(emea|apac|latam)\b/i.test(name) ||
    /^(packaging|chemicals|fragrance)\b/i.test(name)
  ) {
    return null
  }
  return name
}

/** Whole-message tokens that are filters or places, not supplier names. */
const BARE_NAME_STOP =
  /^(high|medium|low|risk|single|sole|source|emea|apac|latam|na|usa|us|u\.s\.|u\.s\.a\.|direct|indirect|procurement|europe|asia|americas|amer|uk|u\.k\.|united states|united kingdom|britain|great britain|korea|south korea|republic of korea|north america|latin america)$/i

/**
 * A message that is only a supplier name, such as "CHEP" or "Gissler & Pass".
 * Questions, list asks, and place or procurement words stay out.
 */
/** “anything I need to urgently manage on MCC” → MCC. */
export function urgentSupplierName(text: string): string | null {
  if (!/\burgent/i.test(text)) return null
  const m = text.match(/\b(?:on|for|about)\s+(.+?)(?:[?.!]|$)/i)
  if (!m?.[1]) return null
  return sanitizeNameCandidate(m[1])
}

export function bareSupplierNameCandidate(text: string): string | null {
  const cleaned = text.replace(/\s+/g, ' ').trim()
  if (!cleaned || cleaned.length > 80) return null
  if (
    /\b(how many|show |list |what are|tell me about|brands?|mitigation|high.?risk|in a table|count|region|what|how|why|when|where|who|which|about|question|compare|explain|summarize|summarise|translate|recommend|tell|give|write|draft)\b/i.test(
      cleaned
    )
  ) {
    return null
  }
  if (!/^[A-Za-z0-9][\w &.,'/()-]{0,78}$/.test(cleaned)) return null
  if (cleaned.split(/\s+/).length > 8) return null
  if (BARE_NAME_STOP.test(cleaned)) return null
  if (supplierBesideDimension(cleaned)) return null
  return sanitizeNameCandidate(cleaned)
}

/**
 * When Risk analyst asked "which supplier?", resolve a follow-up into a name.
 * Handles "What questions should I ask CHEP?" and bare replies like "CHEP".
 */
export function resolvePendingQuestionsSupplier(text: string): string | null {
  const extracted = extractSupplierNameCandidate(text)
  if (extracted) return extracted
  return bareSupplierNameCandidate(text)
}

export function buildAmbiguousSuppliersMessage(names: string[]): string {
  const unique = [...new Set(names)].slice(0, 8)
  const list = unique.map((n) => `- ${n}`).join('\n')
  return `I found multiple suppliers matching that name. Which one do you mean?\n\n${list}`
}

export function buildNoSupplierMessage(query: string): string {
  return `No supplier matching “${query}” was found in platform data. Check the name in Intelligence view, or try a shorter fragment (e.g. “Gissler” or “MM Packaging”).`
}

export function buildAskForSupplierMessage(): string {
  return 'Which supplier’s risk ratings do you want in a table? For example: “Give me the risk assessment of Gissler & Pass GmbH in a table by rating.”'
}

export function buildAskForBrandsSupplierMessage(): string {
  return 'Which supplier’s brands do you want? For example: “What are the brands associated with CHEP?”'
}
